import { FieldValues } from 'react-hook-form'
import { UiSchema } from '@rjsf/utils'
import omit from 'lodash/omit'

import { DropdownField } from '../components/markup/utils/components/material-forms/form-fields/dropdown/dropdown'
import { FormProperties } from '../imup/types'
import { DRAWABLE_SETTINGS_MATERIAL, DRAWABLE_SETTINGS_TYPES } from '../shared/constants/drawable-types'
import { TOOLTIP_FOR_TWL_SIXTEEN_FIELD } from '../shared/constants/form-schema/general'
import { prepareValue } from '../shared/services/opening-form-services'
import { convertFullNumberFractionToDecimal } from '../utils/formSchema/numberConversion'
import { normalizeIJoistBrandSettings } from './utils/iJoistBrandSettings'

const otherValue = 'other'

export const valuesPreparation = (formData: FieldValues) => {
    const settings = { ...formData }

    if (!settings.ribbon) {
        settings.floor_truss_sheathing = false
    }

    if (prepareValue(settings.type) !== 'truss') {
        settings.truss_size = null
        settings.is_treated = null
        settings.ribbon = false
        if (settings.ribbon_dimensions) {
            settings.ribbon_dimensions.length = null
            settings.ribbon_dimensions.width = null
            settings.ribbon_dimensions.thickness = null
        }
        settings.ribbon_other_width = null
        settings.ribbon_other_thickness = null
    }

    if (prepareValue(settings.material) === DRAWABLE_SETTINGS_MATERIAL.DIMENSIONAL) {
        if (prepareValue(settings.bridging_blocking) === 'bridging') {
            settings.blocking_material = null
            if (prepareValue(settings.bridging_material) !== DRAWABLE_SETTINGS_MATERIAL.DIMENSIONAL) {
                if (settings.reinforcement_beam_dimensions !== undefined) {
                    settings.reinforcement_beam_dimensions.thickness = null
                    settings.reinforcement_beam_dimensions.width = null
                    settings.reinforcement_beam_dimensions.length = null
                }
            }
        }

        if (prepareValue(settings.bridging_blocking) === 'blocking') {
            settings.bridging_material = null
        }
    }

    if (
        prepareValue(settings.type) !== DRAWABLE_SETTINGS_TYPES.TRADITIONAL_JOIST &&
        prepareValue(settings.type) !== DRAWABLE_SETTINGS_TYPES.TRADITIONAL_SLEEPER_JOIST
    ) {
        settings.bridging_blocking = null
        if (settings.reinforcement_beam_dimensions !== undefined) {
            settings.reinforcement_beam_dimensions.width = null
            settings.reinforcement_beam_dimensions.thickness = null
            settings.reinforcement_beam_dimensions.length = null
        }
        settings.bridging_material = null
    }

    if (
        prepareValue(settings.material) !== DRAWABLE_SETTINGS_MATERIAL.DIMENSIONAL &&
        prepareValue(settings.sleeper_material) !== DRAWABLE_SETTINGS_MATERIAL.DIMENSIONAL
    ) {
        if (settings.dimensional_joist_dimensions) {
            settings.dimensional_joist_dimensions.thickness = null
            settings.dimensional_joist_dimensions.width = null
        }
    }

    if (prepareValue(settings.material) !== DRAWABLE_SETTINGS_MATERIAL.EWP) {
        if (settings.ewp_joist_dimensions) {
            settings.ewp_joist_dimensions.thickness = null
            settings.ewp_joist_dimensions.width = null
        }
    }

    if (
        prepareValue(settings.type) !== DRAWABLE_SETTINGS_TYPES.TRADITIONAL_JOIST &&
        prepareValue(settings.type) !== DRAWABLE_SETTINGS_TYPES.TRADITIONAL_SLEEPER_JOIST
    ) {
        settings.material = null
    }

    if (prepareValue(settings.type) !== DRAWABLE_SETTINGS_TYPES.I_JOIST) {
        settings.ijoist_size = null
        settings.bci_series = null
        settings.lpi_series = null
        settings.pwi_series = null
        settings.tji_series = null
        settings.ajs_series = null
    }

    if (prepareValue(settings.type) === DRAWABLE_SETTINGS_TYPES.I_JOIST) {
        if (prepareValue(settings.brand).includes('tji')) {
            settings.bci_series = null
            settings.lpi_series = null
            settings.pwi_series = null
            settings.ajs_series = null
        }

        if (prepareValue(settings.brand).includes('bci')) {
            settings.tji_series = null
            settings.lpi_series = null
            settings.pwi_series = null
            settings.ajs_series = null
        }

        if (prepareValue(settings.brand).includes('lpi')) {
            settings.bci_series = null
            settings.tji_series = null
            settings.ajs_series = null
            settings.pwi_series = null
        }

        if (prepareValue(settings.brand).includes('pwi')) {
            settings.bci_series = null
            settings.tji_series = null
            settings.ajs_series = null
            settings.lpi_series = null
        }

        if (prepareValue(settings.brand).includes('ajs')) {
            settings.lpi_series = null
            settings.bci_series = null
            settings.tji_series = null
            settings.pwi_series = null
        }
    }

    return settings
}

export const uiSchema: UiSchema = {
    'ui:order': [
        'type',
        'engineered',
        'material',
        'sleeper_material',
        'dimensional_joist_dimensions',
        'dimensional_other_width',
        'dimensional_other_height',
        'ewp_type',
        'ewp_joist_dimensions',
        'joist_thickness_other',
        'joist_width_other',
        'glulam_type',
        'brand',
        'ajs_series',
        'bci_series',
        'lpi_series',
        'pwi_series',
        'tji_series',
        'ijoist_size',
        'ijoist_size_tji',
        'ijoist_size_ajs',
        'ijoist_size_lpi',
        'ijoist_size_bci',
        'ijoist_size_pwi',
        'oc_spacing',
        'oc_spacing_other',
        'direction',
        'bridging_blocking',
        'bridging_material',
        'reinforcement_beam_dimensions',
        'bridging_beam_dimensions',
        'reinforcement_other_thickness',
        'reinforcement_other_width',
        'truss_size',
        'ribbon',
        'ribbon_dimensions',
        'ribbon_other_thickness',
        'ribbon_other_width',
        'is_treated',
        'floor_truss_sheathing',
    ],
    type: {
        'ui:placeholder': 'Select Floor System Type',
    },
    oc_spacing: {
        'ui:field': DropdownField,
        'ui:placeholder': 'Select O.C Spacing',
        'ui:options': {
            symbol: '"',
        },
    },
    material: {
        'ui:placeholder': 'Select Material',
        'ui:field': 'SectionDivider',
        'ui:options': {
            label: 'Joist',
            dropdownWithPlaceholder: true,
            topDividerFieldType: 'dropdown',
        },
    },
    sleeper_material: {
        'ui:placeholder': 'Select Material',
        'ui:field': 'SectionDivider',
        'ui:options': {
            label: 'Joist',
            dropdownWithPlaceholder: true,
            topDividerFieldType: 'dropdown',
        },
    },
    truss_size: {
        'ui:placeholder': 'Select Size',
        'ui:field': 'SectionDivider',
        'ui:options': {
            label: 'Truss',
            dropdownWithPlaceholder: true,
            topDividerFieldType: 'dropdown',
            symbol: '"',
        },
    },
    ribbon_dimensions: {
        'ui:field': 'LumberDimensionsField',
        'ui:options': {
            tooltips: {
                length: {
                    text: TOOLTIP_FOR_TWL_SIXTEEN_FIELD,
                },
            },
        },
    },
    ribbon: {
        'ui:field': 'SectionDivider',
        'ui:options': {
            topDividerFieldType: 'switch',
            isLabelBold: true,
        },
    },
    board_size: {
        'ui:placeholder': 'Select Board Size',
    },
    dimensional_joist_dimensions: {
        'ui:field': 'LumberDimensionsField',
    },
    ewp_joist_dimensions: {
        'ui:field': 'LumberDimensionsField',
    },
    reinforcement_beam_dimensions: {
        'ui:field': 'LumberDimensionsField',
    },
    bridging_beam_dimensions: {
        'ui:field': 'LumberDimensionsField',
    },
    rimboard: {
        'ui:placeholder': 'Select RimBoard',
    },
    bridging_blocking: {
        'ui:placeholder': 'Select Type',
        'ui:field': 'SectionDivider',
        'ui:options': {
            label: 'Joist Reinforcement',
            dropdownWithPlaceholder: true,
            topDividerFieldType: 'dropdown',
        },
    },
    direction: {
        'ui:field': 'DropdownField',
        'ui:placeholder': 'Select Joist Direction',
    },
    is_treated: {
        'ui:field': 'RadioButtons',
        'ui:options': {
            inline: true,
        },
    },
    ijoist_size: {
        'ui:placeholder': 'Select I-Joist Size',
        'ui:field': DropdownField,
        'ui:options': {
            symbol: '"',
        },
    },
    ijoist_size_tji: {
        'ui:placeholder': 'Select I-Joist Size',
        'ui:field': DropdownField,
        'ui:options': {
            symbol: '"',
        },
    },
    ijoist_size_ajs: {
        'ui:placeholder': 'Select I-Joist Size',
        'ui:field': DropdownField,
        'ui:options': {
            symbol: '"',
        },
    },
    ijoist_size_lpi: {
        'ui:placeholder': 'Select I-Joist Size',
        'ui:field': DropdownField,
        'ui:options': {
            symbol: '"',
        },
    },
    ijoist_size_bci: {
        'ui:placeholder': 'Select I-Joist Size',
        'ui:field': DropdownField,
        'ui:options': {
            symbol: '"',
        },
    },
    ijoist_size_pwi: {
        'ui:placeholder': 'Select I-Joist Size',
        'ui:field': DropdownField,
        'ui:options': {
            symbol: '"',
        },
    },
    ajs_series: {
        'ui:placeholder': 'Select AJS Series',
    },
    bci_series: {
        'ui:placeholder': 'Select BCI Series',
    },
    lpi_series: {
        'ui:placeholder': 'Select LPI Series',
    },
    pwi_series: {
        'ui:placeholder': 'Select PWI Series',
    },
    tji_series: {
        'ui:placeholder': 'Select TJI Series',
    },
    rimboard_ribbon: {
        'ui:placeholder': 'Select Rimboard Ribbon',
    },
    engineered: {
        'ui:field': 'RadioButtons',
        'ui:options': {
            inline: true,
        },
    },
    brand: {
        'ui:placeholder': 'Select Brand',
        'ui:field': 'SectionDivider',
        'ui:options': {
            dropdownWithPlaceholder: true,
            topDividerFieldType: 'dropdown',
            label: 'I-Joist',
        },
    },
    include_rimboard: {
        'ui:title': 'Include Rimboard?',
        'ui:field': 'SwitchField',
    },
    floor_truss_sheathing: {
        'ui:title': 'Floor Truss Sheathing',
        'ui:field': 'SwitchField',
    },
}

const onValidate = () => {}

const onSubmit = (settings, formData) => {
    const newSettings = {
        ...settings,
        ...omit(valuesPreparation(formData), ['quantity']),
    }

    if (newSettings.type.toLowerCase() === 'traditional sleeper joist' && newSettings.sleeper_material) {
        newSettings.material = newSettings.sleeper_material
        newSettings.sleeper_material = null
    }
    if (newSettings.ribbon_dimensions) {
        if (newSettings.ribbon_dimensions.thickness === otherValue) {
            newSettings.ribbon_dimensions.thickness = newSettings.ribbon_other_thickness
        }
        newSettings.ribbon_other_thickness = null

        if (newSettings.ribbon_dimensions.width === otherValue) {
            newSettings.ribbon_dimensions.width = newSettings.ribbon_other_width
        }
        newSettings.ribbon_other_width = null
    }

    if (newSettings.oc_spacing === otherValue) {
        newSettings.oc_spacing = newSettings.oc_spacing_other
    }

    newSettings.oc_spacing_other = null

    if (
        newSettings.ewp_joist_dimensions &&
        newSettings.ewp_joist_dimensions.width != null &&
        newSettings.ewp_joist_dimensions != null
    ) {
        newSettings.joist_dimensions = newSettings.ewp_joist_dimensions
        delete newSettings.ewp_joist_dimensions
    }

    if (
        newSettings.dimensional_joist_dimensions &&
        newSettings.dimensional_joist_dimensions.width != null &&
        newSettings.dimensional_joist_dimensions != null
    ) {
        newSettings.joist_dimensions = newSettings.dimensional_joist_dimensions
        delete newSettings.dimensional_joist_dimensions
    }

    if (newSettings.joist_dimensions) {
        if (newSettings.joist_dimensions.thickness === otherValue) {
            newSettings.joist_dimensions.thickness = convertFullNumberFractionToDecimal(
                newSettings.joist_thickness_other
            )
        }
        newSettings.joist_thickness_other = null

        if (newSettings.joist_dimensions.width === otherValue) {
            newSettings.joist_dimensions.width = convertFullNumberFractionToDecimal(newSettings.joist_width_other)
        }
        newSettings.joist_width_other = null
    }
    if (newSettings.bridging_beam_dimensions) {
        newSettings.reinforcement_beam_dimensions = newSettings.bridging_beam_dimensions
        delete newSettings.bridging_beam_dimensions
    }

    if (newSettings.reinforcement_beam_dimensions) {
        if (newSettings.reinforcement_beam_dimensions.thickness === otherValue) {
            newSettings.reinforcement_beam_dimensions.thickness = newSettings.reinforcement_other_thickness
        }
        newSettings.reinforcement_other_thickness = null

        if (newSettings.reinforcement_beam_dimensions.width === otherValue) {
            newSettings.reinforcement_beam_dimensions.width = newSettings.reinforcement_other_width
        }
        newSettings.reinforcement_other_width = null
    }

    newSettings.series = null
    normalizeIJoistBrandSettings(newSettings)

    return newSettings
}

const form: FormProperties = { uiSchema, onValidate, onSubmit }

export default form
